import React, { useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntitySupportingCard } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import EntityCard from "@app/component/RecordCard/components/EntityCard";
import RecordPaneTab from "@app/enum/preview/RecordPaneTab";
import { showEntityView } from "@app/store/entityView/actions";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import styles from "./RelationshipEntityCard.module.scss";

interface Props {
  entity: EntitySupportingCard;
  isReadOnlyMember: boolean;
  onRemove?: (entity: EntitySupportingCard) => void;
  primary?: boolean;
  showEntityView: typeof showEntityView;
}

const RelationshipEntityCard: React.FC<Props> = ({
  entity,
  isReadOnlyMember,
  onRemove,
  primary,
  showEntityView,
}) => {
  const intl = useIntl();

  const [hovered, setHovered, unsetHovered] = useBoolean();

  const handleRemove = useCallback(() => onRemove?.(entity), [entity, onRemove]);

  const handleCompanyClick = useCallback(() => {
    showEntityView({ entityId: entity.id, entityType: entity.entity, tab: RecordPaneTab.DETAILS });
  }, [entity.id, entity.entity, showEntityView]);

  return (
    <EntityCard
      className={styles.item}
      entity={entity}
      isHighlighted={hovered}
      isRemoveActionVisible={!isReadOnlyMember && entity.accessStatus?.delete}
      onClick={handleCompanyClick}
      onHover={setHovered}
      onLeave={unsetHovered}
      onRemove={handleRemove}
      primary={primary}
      removeActionConfirmationText={intl.formatMessage({
        id: "recordPane.relationship.confirmation.remove.confirmation",
        defaultMessage: "Are you sure you want to remove this relationship?",
        description: "Entity preview pane - relationship - remove confirmation text",
      })}
      removeActionHoverText={intl.formatMessage({
        id: "recordPane.relationship.confirmation.remove.hover",
        defaultMessage: "Remove relationship",
        description: "Entity preview pane - relationship - remove hover text",
      })}
      showDistance
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

const mapDispatchToProps = {
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(RelationshipEntityCard);
